import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface CacheEntry<T> {
  data: T;
  lastRefreshed: string; // ISO 8601 UTC timestamp
}

interface AdminCacheDB extends DBSchema {
  cache: {
    key: string;
    value: CacheEntry<any>;
  };
}

let dbPromise: Promise<IDBPDatabase<AdminCacheDB>> | null = null;

function getDB(): Promise<IDBPDatabase<AdminCacheDB>> {
  if (!dbPromise) {
    dbPromise = openDB<AdminCacheDB>('admin-cache', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('cache')) {
          db.createObjectStore('cache');
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Get cached data by key
 * @param key - Cache key
 * @returns Cached data with metadata, or null if not found
 */
export async function getCachedData<T>(key: string): Promise<{ data: T; lastRefreshed: Date } | null> {
  try {
    const db = await getDB();
    const entry = await db.get('cache', key);
    
    if (!entry) {
      return null;
    }
    
    return {
      data: entry.data as T,
      lastRefreshed: new Date(entry.lastRefreshed),
    };
  } catch (error) {
    console.error(`[CacheService] Error getting cached data for key "${key}":`, error);
    return null;
  }
}

/**
 * Set cached data with timestamp
 * @param key - Cache key
 * @param data - Data to cache
 */
export async function setCachedData<T>(key: string, data: T): Promise<void> {
  try {
    const db = await getDB();
    const entry: CacheEntry<T> = {
      data,
      lastRefreshed: new Date().toISOString(),
    };
    await db.put('cache', entry, key);
    if (typeof window !== 'undefined' && key === 'all_users') {
      window.dispatchEvent(new CustomEvent('admin:cacheUpdated', { detail: { key: 'all_users' } }));
    }
  } catch (error) {
    console.error(`[CacheService] Error setting cached data for key "${key}":`, error);
  }
}

/**
 * Clear all cached data
 */
export async function clearCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear('cache');
    console.log('[CacheService] All cache cleared');
  } catch (error) {
    console.error('[CacheService] Error clearing cache:', error);
  }
}

/**
 * Clear cached data for a specific key
 * @param key - Cache key to clear
 */
export async function clearCacheKey(key: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('cache', key);
    console.log(`[CacheService] Cache cleared for key "${key}"`);
  } catch (error) {
    console.error(`[CacheService] Error clearing cache for key "${key}":`, error);
  }
}

/**
 * Update a specific user in cache.
 * Updates both the all_users list and the per-user entry (user_${userId}) so that
 * the list and the user detail view stay in sync after edits.
 * @param userId - User ID to update
 * @param updatedUser - Updated user data (full user object as returned by API)
 */
export async function updateUserInCache(userId: string, updatedUser: any): Promise<void> {
  try {
    const db = await getDB();
    const cached = await db.get('cache', 'all_users');

    if (cached && cached.data && Array.isArray(cached.data.users)) {
      const users = [...cached.data.users];
      const index = users.findIndex((u: any) => u.userId === userId);

      if (index !== -1) {
        users[index] = { ...users[index], ...updatedUser };
        await setCachedData('all_users', { users });
      }
    }

    // Keep per-user cache in sync so reopening the same user shows fresh data
    await setCachedData(`user_${userId}`, { user: updatedUser });

    console.log(`[CacheService] Updated user ${userId} in cache`);
  } catch (error) {
    console.error(`[CacheService] Error updating user in cache:`, error);
  }
}
